import React, {CSSProperties, useEffect, useRef} from 'react';
import AutoNumeric from 'autonumeric';
import {useFormikContext} from 'formik';
import {FormikInputField} from '@payactive/app-common';
import {InputAdornment, TextFieldProps} from '@material-ui/core';
import {InputFieldProps} from '@payactive/app-common/dist/input/InputField';

type Props = InputFieldProps &
  TextFieldProps & {
  name: string;
  label: string;
  currencySymbol: string;
  value?: number;
  className?: string;
  style?: CSSProperties;
  required?: boolean;
  fullWidth?: boolean;
};

export default function CurrencyTextField(props: Props) {
  const { name, value } = props;
  const { values, setFieldValue, setFieldTouched } = useFormikContext<any>();
  const { currencySymbol, ...forwardProps } = props;
  const currencyInputRef = useRef<HTMLInputElement | null>(null);
  const autoNumericRef = useRef<AutoNumeric | null>(null);

  const initial = value ?? name.split(".").reduce((o, i) => (o ? o[i] : undefined), values);
  const valRef = useRef(initial);

  useEffect(() => {
    const el = currencyInputRef.current;
    if (!el) return;

    const an = new AutoNumeric(el, valRef.current ?? "", {
      ...AutoNumeric.getPredefinedOptions().commaDecimalCharDotSeparator,
      minimumValue: "0",
      caretPositionOnFocus: "start",
      digitGroupSeparator: "",
    });

    autoNumericRef.current = an;

    const onInput = () => {
      const n = an.getNumber();
      if (n !== valRef.current) {
        valRef.current = n
        setFieldValue(name, n, false);
      }
    };

    const onBlur = () => setFieldTouched(name, true, true);

    el.addEventListener("input", onInput);
    el.addEventListener("blur", onBlur);

    return () => {
      el.removeEventListener("input", onInput);
      el.removeEventListener("blur", onBlur);
      an.remove();
      autoNumericRef.current = null;
    };
  }, [name, setFieldValue, setFieldTouched]);

  return (
    <>
      <FormikInputField // needs to be an uncontrolled field, setting the values via eventListeners
        {...forwardProps}
        value={undefined as any} // needs to be undefined to prevent formik from setting the value
        onChange={undefined as any} // needs to be undefined to prevent formik from setting the value
        inputRef={currencyInputRef}
        placeholder={'0,00'}
        InputProps={{
          startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment>,
        }}
      />
    </>
  );
}